'use client';

import React, { useRef, useState, useLayoutEffect } from 'react';
import classnames from 'classnames';

import Typography from '../Typography/Typography';
import Box from '../Box/Box';
import Spec from '../Spec/Spec';
import { ImageProps } from '../Image/Image';
import Iconography, { IconographyProps } from '../Iconography/Iconography';
import { dataLayerHandler } from '../../../utils/analytics.ts';

import styles from './Swatch.module.scss';
import Skeleton from '../Skeleton/Skeleton';

export interface SwatchLayoutResponsive {
  xs?: 'horizontal' | 'vertical';
  md?: 'horizontal' | 'vertical';
  xl?: 'horizontal' | 'vertical';
}

export interface SwatchProps {
  theme?: 'dark' | 'light';
  /**
   * The 'shape' prop is equivalent to the 'type' prop in Figma.
   */
  shape?: 'circle' | 'rectangle' | 'icon';
  clickable?: boolean;
  layout?: 'horizontal' | 'vertical' | SwatchLayoutResponsive;
  size?: 'extraSmall' | 'small' | 'medium' | 'large' | 'extraLarge';
  specSize?: 'extraSmall' | 'small' | 'medium' | 'large';
  image?: ImageProps;
  icon?: IconographyProps;
  title?: string;
  titleIcon?: IconographyProps;
  description?: string;
  disclaimer?: string;
  label?: string;
  active?: boolean;
  disabled?: boolean;
  className?: string;
  /**
   * Use a skeleton style (see use cases for VehicleCard on the compare page)
   */
  skeleton?: boolean;
  direction?: 'ltr' | 'rtl';
  dataTestId?: string;
  handleClick?: () => void;
  gaComponent?: string;
  linkContext?: string;
  handleIconClick?: () => void;
  showSkeleton?: boolean;
  logEvents?: boolean;
}

const Swatch: React.FC<SwatchProps> = ({
  shape = 'circle',
  clickable = false,
  size = 'medium',
  specSize = 'small',
  layout = 'horizontal',
  image,
  icon = { category: 'global', name: 'information' },
  titleIcon,
  title = '',
  description = '',
  disclaimer = '',
  label = '',
  active = false,
  disabled = false,
  skeleton = false,
  direction = 'ltr',
  className = '',
  dataTestId = 'swatch',
  handleClick = () => {},
  handleIconClick = () => {},
  gaComponent = 'Swatch', // for use with datalayer
  linkContext = '', // for use with datalayer
  theme = 'dark',
  showSkeleton = false,
  logEvents = true,
}) => {
  const labelRef = useRef<HTMLDivElement>(null);
  const [align, setAlign] = useState<'left' | 'center' | 'right'>('center');

  useLayoutEffect(() => {
    const measure = () => {
      if (!labelRef.current) return;
      const vw = window.innerWidth;

      const labelRect = labelRef.current.getBoundingClientRect();

      if (labelRect.left < 0) setAlign('left');
      else if (labelRect.right > vw) setAlign('right');
      else setAlign('center');
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [label]);

  const hasContent = title || description || (image && (image.uri_desktop || image.uri_mobile));
  const useIcon = shape === 'icon';

  const renderIcon = (iconProps: IconographyProps | undefined) => {
    if (!iconProps) return null;
    return (
      <Iconography
        size={undefined} // Disable default sizing
        color={theme === 'dark' ? 'white' : 'black'}
        {...iconProps}
      />
    );
  };

  const HtmlTag = clickable ? 'button' : 'div';

  const getSizeClasses = (layout: 'horizontal' | 'vertical' | SwatchLayoutResponsive) => {
    let sizeClassList: string[] = [];

    if (typeof layout === 'string') {
      sizeClassList = [...sizeClassList, styles[`${layout}`]];
    } else {
      for (const size in layout) {
        const key = size as keyof SwatchLayoutResponsive;
        const value = layout[key];
        if (value) {
          sizeClassList = [...sizeClassList, styles[`${size}-${value}`]];
        }
      }
    }

    return sizeClassList;
  };

  const handleOnClick = () => {
    if (disabled || !clickable) return;
    logEvents &&
      dataLayerHandler({
        interactionType: 'selection',
        gaComponent,
        label: label || title || description,
        linkContext,
      });
    handleClick();
  };

  if (!hasContent && !clickable) {
    return <Spec title="—" size={specSize} direction={direction} />;
  }

  const TagComponent = HtmlTag as keyof React.JSX.IntrinsicElements;

  return (
    <TagComponent
      className={classnames(
        getSizeClasses(layout),
        {
          [styles.container]: true,
          [styles.clickable]: clickable,
          [styles.skeleton]: skeleton,
          [styles.disabled]: disabled,
          [styles['direction-rtl']]: direction === 'rtl',
        },
        className
      )}
      onClick={handleOnClick}
      aria-label={label || title || description}
      data-testid={dataTestId}
      {...(HtmlTag === 'div' && { role: 'button' })}
    >
      {(skeleton || image || useIcon) && (
        <Box className={styles.thumbnailLabelContainer} direction={direction}>
          {showSkeleton ? (
            <Skeleton
              className={classnames(styles.thumbnailWrapper, styles[shape], styles[size])}
              height="revert-layer"
              borderRadius="50px"
              theme={theme}
            />
          ) : (
            <Box
              className={classnames(
                styles.thumbnailWrapper,
                styles[shape],
                styles[size],
                styles[theme],
                {
                  [styles.clickable]: clickable,
                  [styles.active]: !disabled && active,
                  [styles.disabled]: disabled,
                  [styles.skeleton]: skeleton,
                }
              )}
              pt={4}
              pb={4}
              pl={4}
              pr={4}
              direction={direction}
            >
              {!skeleton && !useIcon && image && (
                <Box className={styles.thumbnail}>
                  <picture>
                    <source
                      srcSet={`${image.uri_mobile?.['1x']} 1x, ${image.uri_mobile?.['2x']} 2x`}
                      media="(max-width: 768px)"
                      type="image/webp"
                    />
                    <source
                      srcSet={`${image.uri_desktop?.['1x']} 1x, ${image.uri_desktop?.['2x']} 2x`}
                      media="(min-width: 768px)"
                      type="image/webp"
                    />
                    <img src={image.uri_desktop?.['1x']} alt={image.alt} />
                  </picture>
                </Box>
              )}
              {!skeleton && useIcon && renderIcon(icon)}
            </Box>
          )}
          {clickable && label && (
            <div ref={labelRef} className={classnames(styles.label, styles[align])}>
              <Typography
                variant="body"
                size="extraSmall"
                theme={theme}
                color="white"
                direction={direction}
              >
                {label}
              </Typography>
            </div>
          )}
        </Box>
      )}

      {(title || description) && (
        <Box direction={direction}>
          <Spec
            title={title}
            description={description}
            disclaimer={disclaimer}
            size={specSize}
            direction={direction}
            icon={titleIcon}
            handleIconClick={handleIconClick}
            theme={theme}
            showSkeleton={showSkeleton}
          />
        </Box>
      )}
    </TagComponent>
  );
};

export default Swatch;
